import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuCheck, LuX, LuTruck, LuRadio, LuMapPin, LuBell } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import '../industry.css';

const PAIN = [
  'Twilio at $0.0079+/SMS — drivers, brokers, and shipper updates add up fast',
  '10DLC registration for a fleet of 5? Two months of paperwork before you send one text',
  'Aggregator long codes flagged as spam — load updates landing in junk',
  'No native dispatcher inbox for two-way conversations with the road',
];

const GAIN = [
  'Real consumer SIM, $0.001/SMS — same plan you already pay for the office phone',
  'Zero registration. Pair the phone, send the text, done',
  'P2P traffic from a real number — drivers actually see the messages',
  'Browser inbox for the dispatch desk, threaded by driver',
];

const DAY = [
  { time: '6:12 AM', title: 'Dispatch sends load assignments', body: 'Bulk SMS to 24 drivers with pickup time, address, and load number. Each one personalized via merge fields.' },
  { time: '8:03 AM', title: 'Driver replies "stuck at scale"', body: 'Reply lands in dispatcher\'s browser inbox in under a second. Threaded under the driver\'s name, not a random aggregator number.' },
  { time: '11:47 AM', title: 'ETA update to shipper', body: 'API call from the TMS pings the shipper: "Driver running 30 min late on PO #18472." Status webhook confirms delivered.' },
  { time: '2:30 PM', title: 'Detention alert', body: 'Driver auto-texts dispatch from the ELD app when stopped >2 hrs. Dispatch escalates with one tap.' },
  { time: '5:55 PM', title: 'POD photo notification', body: 'Driver delivers, snaps the BOL, MMS lands at the broker. Same SIM, same conversation.' },
];

const FAQ = [
  {
    q: 'Will my drivers see weird-looking sender numbers?',
    a: 'No. The SMS goes through a real consumer SIM you control — the number you pick is the number drivers see. No aggregator long codes, no random short codes.',
  },
  {
    q: 'How does this work with TMS like McLeod, Trucker Tools, or KeepTruckin?',
    a: 'Use our REST API to send from any TMS. Inbound replies fire a webhook back to your endpoint. Most dispatchers wire it up in under an afternoon.',
  },
  {
    q: 'What if the SIM\'s phone dies overnight?',
    a: 'We notify you the moment a paired phone goes offline. Most fleets keep two cheap Androids paired with the same carrier — automatic failover during charging.',
  },
  {
    q: 'Can multiple dispatchers share the same SMS inbox?',
    a: 'Yes. Add sub-users in the dashboard and assign SIMs per dispatcher, or share a SIM across a whole shift. Each dispatcher only sees the threads on their assigned SIMs.',
  },
];

export default function TruckingDispatch() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="SMS for Trucking & Dispatch — Driver & Load Updates"
        description="SMS gateway built for trucking dispatch. Send load assignments, ETA updates, and POD confirmations through your own SIM at $0.001 per message. No 10DLC paperwork, browser dispatcher inbox, REST API into your TMS."
        canonical="/trucking"
      />
      <HeaderOne />

      <main className="iv">
        <div className="iv-shell">

          {/* HERO */}
          <header className="iv-hero">
            <div className="iv-hero-left">
              <span className="iv-eyebrow">Trucking & dispatch</span>
              <h1 className="iv-h1">
                The SMS layer<br />
                <span className="iv-h1-accent">your dispatch desk needs.</span>
              </h1>
              <p className="iv-lead">
                Load assignments, driver updates, ETA pings to shippers, POD photos — all running through your own SIM at $0.001 per SMS. No 10DLC paperwork, no Twilio fees, native two-way inbox.
              </p>
              <div className="iv-cta-row">
                <button className="iv-btn iv-btn--primary" onClick={() => navigate('/signup')}>
                  Try free <LuArrowRight size={15} strokeWidth={2} />
                </button>
                <Link className="iv-btn iv-btn--ghost" to="/how-textlink-works">How it works</Link>
              </div>
            </div>

            <div className="iv-hero-stats">
              <div className="iv-stat-tile iv-stat-tile--accent">
                <div className="iv-stat-num">$0.001</div>
                <div className="iv-stat-label">per SMS, per driver, per dispatcher — flat</div>
              </div>
              <div className="iv-stat-tile">
                <div className="iv-stat-num">5 min</div>
                <div className="iv-stat-label">setup. No 10DLC, no A2P registration.</div>
              </div>
              <div className="iv-stat-tile">
                <div className="iv-stat-num">98%</div>
                <div className="iv-stat-label">delivery rate on real consumer SIMs</div>
              </div>
              <div className="iv-stat-tile iv-stat-tile--accent">
                <div className="iv-stat-num">2-way</div>
                <div className="iv-stat-label">browser inbox threaded by driver</div>
              </div>
            </div>
          </header>

          {/* PAIN / GAIN */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">The status quo is brutal for fleets</h2>
              <p className="iv-sub">Aggregator pricing, registration paperwork, and spam-flagged long codes were not built for dispatch.</p>
            </div>

            <div className="iv-contrast">
              <div className="iv-contrast-panel iv-contrast-panel--bad">
                <span className="iv-contrast-eyebrow">Twilio + Aggregators</span>
                <h3 className="iv-contrast-h3">What you're stuck with today</h3>
                <ul className="iv-contrast-list">
                  {PAIN.map((p, i) => (
                    <li key={i}>
                      <span className="iv-contrast-icon"><LuX size={12} strokeWidth={2.5} /></span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="iv-contrast-panel iv-contrast-panel--good">
                <span className="iv-contrast-eyebrow">TextLink</span>
                <h3 className="iv-contrast-h3">What changes with your own SIM</h3>
                <ul className="iv-contrast-list">
                  {GAIN.map((g, i) => (
                    <li key={i}>
                      <span className="iv-contrast-icon"><LuCheck size={12} strokeWidth={2.5} /></span>
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* DAY IN THE LIFE */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">A day on the dispatch desk</h2>
              <p className="iv-sub">What SMS through TextLink looks like across one shift, from morning load board to evening POD.</p>
            </div>

            <ul className="iv-day">
              {DAY.map((d, i) => (
                <li key={i} className="iv-day-row">
                  <div className="iv-day-time">{d.time}</div>
                  <div className="iv-day-body">
                    <h3 className="iv-day-title">{d.title}</h3>
                    <p className="iv-day-text">{d.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* NUMBERS */}
          <section className="iv-section">
            <div className="iv-numbers">
              <div className="iv-num">
                <div className="iv-num-big"><span>90%</span></div>
                <div className="iv-num-label">cheaper than Twilio at fleet volume</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">5 min</div>
                <div className="iv-num-label">from signup to first dispatch SMS</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">$60</div>
                <div className="iv-num-label">used Android — that's all the hardware you need</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">∞</div>
                <div className="iv-num-label">drivers per SIM, dispatchers per inbox</div>
              </div>
            </div>
          </section>

          {/* USE CASES — alternating rows */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">Built for the work you actually do</h2>
              <p className="iv-sub">Three workflows that show up in every fleet, dialed in for SMS.</p>
            </div>

            <div className="iv-cases">

              <div className="iv-case">
                <div className="iv-case-text">
                  <span className="iv-case-tag">Load assignments</span>
                  <h3 className="iv-case-h3">Push assignments at shift start, in seconds.</h3>
                  <p className="iv-case-p">
                    Upload your driver list, write a template with merge fields, send. Each driver gets a personalized text with their pickup, drop, and load number — sent through a real number, not a random shortcode.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> Bulk SMS, paced so carriers stay happy</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Personalized via {`{{first_name}}`}, {`{{load_id}}`}, anything from your CSV</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Replies thread back to the dispatcher inbox automatically</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Outbound — 6:12 AM</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Hey Mark, your load today: pickup 7am @ 2200 Industry Blvd, drop @ Reno DC by 4pm. Load #18472. Reply STUCK if anything goes wrong.
                    </div>
                    <div className="iv-msg-meta iv-msg-meta--right">Delivered ✓</div>
                  </div>
                </div>
              </div>

              <div className="iv-case iv-case--reverse">
                <div className="iv-case-text">
                  <span className="iv-case-tag">Driver communication</span>
                  <h3 className="iv-case-h3">Real two-way conversations from the desk.</h3>
                  <p className="iv-case-p">
                    Drivers text back like they would a friend — same SIM, same number, threaded by name in the dispatcher's browser. No "did the message go through?" guessing, no aggregator black-box.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> Multi-dispatcher inbox, assigned per SIM</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> MMS for POD photos, BOLs, scale tickets</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Real-time delivery status — sent, delivered, read</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Inbound — 8:03 AM</div>
                    <div className="iv-msg-bubble">
                      Stuck at the scale, gonna be 20-30 late on the Reno drop. Should I call ahead?
                    </div>
                    <div className="iv-msg-meta">Mark — driver, route 18</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Yeah, give Reno DC a heads up. I'll move your 4pm appt to 5.
                    </div>
                  </div>
                </div>
              </div>

              <div className="iv-case">
                <div className="iv-case-text">
                  <span className="iv-case-tag">Shipper / broker updates</span>
                  <h3 className="iv-case-h3">Auto-update shippers from your TMS.</h3>
                  <p className="iv-case-p">
                    Wire the SMS API into your TMS or load board. ETA updates, detention alerts, and POD confirmations fire automatically — your dispatchers stay focused on the road, not the keyboard.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> REST API works from any TMS — McLeod, KeepTruckin, custom</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Status webhooks back to your system</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Works with Zapier and Make for no-code wiring</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Outbound — 11:47 AM</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Update on PO #18472: driver delayed at scale. New ETA 5:00 PM (was 4:00 PM). Reply STOP to opt out of updates.
                    </div>
                    <div className="iv-msg-meta iv-msg-meta--right">Auto-sent by TMS</div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* FAQ */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">Common dispatch questions</h2>
              <p className="iv-sub">Everything fleets ask before signing up.</p>
            </div>

            <div className="iv-faq">
              {FAQ.map((f, i) => (
                <div key={i} className="iv-faq-item">
                  <h3 className="iv-faq-q">{f.q}</h3>
                  <p className="iv-faq-a">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="iv-section">
            <div className="iv-final">
              <h2>Ditch Twilio. Pair an Android. Dispatch.</h2>
              <p>Five minutes from signup to the first load assignment.</p>
              <div className="iv-cta-row">
                <button className="iv-btn iv-btn--white" onClick={() => navigate('/signup')}>
                  Try free <LuArrowRight size={15} strokeWidth={2} />
                </button>
                <Link className="iv-btn iv-btn--white-ghost" to="/pricing">See pricing</Link>
              </div>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
